import { gql } from '@apollo/client';

import '@cobliteam/common-types';

import type {
  Category,
  ExpensePaginatedResponse,
  ExpensesListInput,
  ImportBatchResponse,
  ImportExpensesBatchInput,
  Supplier,
  ValidateExpensesImportInput,
  ValidateExpensesImportResponse,
  VehicleExpensesPaginatedResponse,
  VehiclesExpensesInput,
} from '../../__generated__/graphql';
import graphQLRequester from '../../request/GraphQLRequester';
import TimeZone from '../../request/Timezone';
import type { GraphQLRestDataSourceError } from '../../request/Types';

export class GraphQLTCORequester {
  public getExpenses = (input: ExpensesListInput): Promise<ExpensePaginatedResponse> =>
    graphQLRequester
      .query<{
        expenses: ExpensePaginatedResponse;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        query: gql`
          query expenses($input: ExpensesListInput!) {
            expenses(input: $input) {
                content {
                  id
                  occurredAt
                  vehicle {
                    id
                    licensePlate
                    groupPath
                    groupName
                    tagNames
                    model
                    brand
                    size
                    alias
                  }
                  category {
                    id
                    name
                  }
                  type
                  totalCost
                  supplier {
                    id
                    name
                  }
                  observation
                  source
                  createdAt
                  createdBy
                  updatedAt
                  updatedBy
                  deletedAt
                  deletedBy
                }
                number
                size
                totalElements
                totalPages
            }
          }
        `,
        variables: { input },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.expenses;
      });

  public getCategories = (): Promise<Category[]> =>
    graphQLRequester
      .query<{
        categories: Category[];
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        query: gql`
          query categories {
            categories {
              id
              name
            }
          }
        `,
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.categories;
      });

  public getSuppliers = (): Promise<Supplier[]> =>
    graphQLRequester
      .query<{
        suppliers: Supplier[];
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        query: gql`
            query suppliers {
              suppliers {
                id
                name
              }
            }
          `,
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.suppliers;
      });

  public getVehicleExpenses = (input: Omit<VehiclesExpensesInput, 'timezone'>): Promise<VehicleExpensesPaginatedResponse> =>
    graphQLRequester
      .query<{
        vehicleExpenses: VehicleExpensesPaginatedResponse;
        errors: Array<GraphQLRestDataSourceError>;
      }>({
        query: gql`
          query vehicleExpenses($input: VehiclesExpensesInput!) {
            vehicleExpenses(input: $input) {
              content {
                categoryCosts {
                  cost
                  id
                  name
                }
                costPerKm
                previousPeriodCostPerKm
                previousPeriodTotalCost
                distanceDrivenInKm
                totalCost
                vehicle {
                  id
                  licensePlate
                  size
                  groupPath
                  groupName
                  tagNames
                  model
                  brand
                  alias
                }
              }
              empty
              first
              last
              number
              numberOfElements
              size
              totalElements
              totalPages
            }
          }
        `,
        variables: {
          input: {
            timezone: TimeZone.getTimezone(),
            ...input,
          },
        },
      })
      .then((result) => {
        if (result.errors !== undefined) {
          throw result.errors[0];
        }
        return result.vehicleExpenses;
      });

  public validateExpensesImport = (input: ValidateExpensesImportInput): Promise<ValidateExpensesImportResponse> =>
    graphQLRequester
      .query<{
        validateExpensesImport: ValidateExpensesImportResponse;
      }>({
        query: gql`
          query ValidateExpensesImport($input: ValidateExpensesImportInput!) {
            validateExpensesImport(input: $input) {
              validRows {
                occurredAt
                vehicleId
                groupPath
                categoryId
                type
                totalCost
                supplierName
                observation
              }
              invalidRows {
                data {
                  occurredAt
                  licensePlate
                  categoryName
                  type
                  totalCost
                  supplierName
                  observation
                  rowIndex
                }
                errors {
                  error
                  column {
                    name
                    position
                  }
                }
              }
            }
          }`,
        variables: {
          input: input,
        },
      })
      .then((result) => result.validateExpensesImport);

  public importExpensesBatch = (input: ImportExpensesBatchInput[]): Promise<ImportBatchResponse> =>
    graphQLRequester
      .mutate<{ createdExpenses: ImportBatchResponse }, { input: ImportExpensesBatchInput[] }>({
        mutation: gql`
          mutation Mutation($input: [ImportExpensesBatchInput!]!) {
            importExpenses(input: $input) {
              count
            }
          }
        `,
        variables: {
          input: input,
        },
      })
      .then((result) => result.createdExpenses);
}

export default GraphQLTCORequester;
